import { cloneDeep, uniq } from "lodash";
import React, { useCallback, useEffect, useMemo } from "react";
import { useForm, useWatch } from "react-hook-form";
import { UserSummaryFragment } from "../../graphql/fragments";
import { ReportType } from "../../graphql/types";
import useTranslations from "../../hooks/useTranslations";
import { DeleteIcon, DotVerticalIcon } from "../icons";
import UserSearch from "../utils/search/UserSearch";
import {
  Avatar,
  Button,
  Checkbox,
  CheckboxGroup,
  Input,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from "../wombat-ui";

const SelectedReferent: React.FC<{
  user: UserSummaryFragment;
  handleUserDelete?: (id: string) => void;
  handleAppendUserToAll?: (id: string) => void;
  baseId?: string;
}> = ({ user, baseId, handleAppendUserToAll, handleUserDelete }) => {
  const t = useTranslations();

  return (
    <div
      key={user.id}
      className="flex items-center justify-between w-full max-w-lg p-2 rounded-sm bg-light-0 lg:max-w-sm"
    >
      <div className="flex items-center">
        <Avatar src={user.avatar} alt="" size={6} type="user" />
        <p className="ml-4 text-sm font-medium text-dark-100">{user.name}</p>
      </div>
      <Menu>
        <MenuButton
          id={`${baseId}${user.id}-open-options`}
          icon={DotVerticalIcon}
          variant="ghost"
        ></MenuButton>
        <MenuList>
          <MenuItem
            onSelect={() => handleUserDelete(user.id)}
            id={`${baseId}${user.id}-user-remove`}
          >
            {t("forms.actions.delete")}
          </MenuItem>
          <MenuItem
            id={`${baseId}${user.id}-user-append-to-all`}
            onSelect={() => handleAppendUserToAll(user.id)}
          >
            {t("mads.addReferentToEveryQuestions")}
          </MenuItem>
        </MenuList>
      </Menu>
    </div>
  );
};

type TemplateQuestionFormFooterProps = {
  register: ReturnType<typeof useForm>["register"];
  unregister: ReturnType<typeof useForm>["unregister"];
  setValue: ReturnType<typeof useForm>["setValue"];
  getValues: ReturnType<typeof useForm>["getValues"];
  reset: ReturnType<typeof useForm>["reset"];
  control: ReturnType<typeof useForm>["control"];
  shouldSelectReferent: boolean;
  reportTypes: ReportType[];
  referentIds: string[];
  timeTracked: boolean;
  expectedTimeValue: number;
  onRemove: () => void;
  name: string;
};

const TemplateQuestionFormFooter: React.FC<TemplateQuestionFormFooterProps> = ({
  register,
  setValue,
  getValues,
  reset,
  control,
  onRemove,
  shouldSelectReferent,
  reportTypes,
  timeTracked,
  expectedTimeValue,
  referentIds = [],
  name,
}) => {
  const t = useTranslations();

  const hasTimeTracked = useWatch({
    name: `${name}.timeTracked`,
    control,
    defaultValue: timeTracked,
  });

  const _ = useWatch({
    name: `templateAttributes.templateSectionsAttributes`,
    control,
  });

  useEffect(() => {
    register(`${name}.referentIds`);
    setValue(`${name}.referentIds`, referentIds);
  }, [referentIds]);

  const emphasizedUsers = useMemo(
    () => [
      {
        title: t("mads.previouslySelected") as string,
        elements: [
          ...uniq(
            getValues()?.templateAttributes?.templateSectionsAttributes?.flatMap(
              ({ templateQuestionsAttributes }) =>
                templateQuestionsAttributes?.flatMap(
                  ({ referentIds }) => referentIds
                )
            )
          ),
        ] as string[],
      },
    ],
    [_]
  );

  const appendReferentToEveryQuestion = useCallback(
    (userId: string) => {
      // Clone values to avoid dealing with Apollo's frozen objects
      const values = cloneDeep(getValues());

      values.templateAttributes.templateSectionsAttributes = values.templateAttributes?.templateSectionsAttributes.map(
        ({ templateQuestionsAttributes, ...restSectionAttributes }) => ({
          ...restSectionAttributes,
          templateQuestionsAttributes:
            templateQuestionsAttributes?.map(
              ({ referentIds, ...restTemplateQuestionAttributes }) => ({
                ...restTemplateQuestionAttributes,
                referentIds: [...uniq([...referentIds, userId])],
              })
            ) || [],
        })
      );

      reset(values);
    },
    [reset, getValues]
  );

  return (
    <div className={`flex flex-col px-6 py-6 space-y-4`}>
      <div className="flex flex-col space-y-4">
        {shouldSelectReferent && (
          <div>
            <p className="pb-2 text-sm font-medium text-dark-100">
              {t("mads.selectQuestionReferents")}
            </p>
            <UserSearch
              defaultValue={referentIds}
              emphasized={emphasizedUsers}
              baseId={`${name}.referent.`}
              onChange={(value) => {
                setValue(
                  `${name}.referentIds`,
                  value.map(({ id }) => id)
                );
              }}
              renderSelectedItem={(user, _, onRemove) => (
                <SelectedReferent
                  key={user.id}
                  baseId={`${name}.referent.`}
                  user={user}
                  handleUserDelete={onRemove}
                  handleAppendUserToAll={appendReferentToEveryQuestion}
                />
              )}
            />
          </div>
        )}
        <div>
          <p className="text-sm font-medium text-dark-100">
            {t("mads.advancedOptions")}
          </p>
          <div className="flex flex-col">
            <Checkbox
              label={t("mads.timeTracked") as string}
              help={t("mads.timeTrackedDescription") as string}
              id={`${name}.timeTracked`}
              name={`${name}.timeTracked`}
              ref={register()}
              variant="boxed"
              defaultChecked={timeTracked}
            />
            {hasTimeTracked && (
              <div className="flex flex-col pt-1 pl-3 space-y-2">
                <p className="text-xs font-medium text-dark-100">
                  {t("mads.expectedTimeValue") as string}
                </p>
                <Input
                  size="xsmall"
                  ref={register({ valueAsNumber: true })}
                  label={t("mads.expectedTimeValue") as string}
                  srOnlyLabel
                  id={`${name}.expectedTimeValue`}
                  name={`${name}.expectedTimeValue`}
                  type="number"
                  defaultValue={expectedTimeValue}
                />
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="flex justify-between">
        <CheckboxGroup
          display="row"
          title={t("mads.whichReport") as string}
          description={t("mads.whichReportDescription") as string}
        >
          <Checkbox
            label={t("reports.header.FirstReport") as string}
            id={`${name}.reportTypes_onboarding`}
            name={`${name}.reportTypes`}
            value={"FirstReport"}
            defaultChecked={reportTypes.includes("FirstReport")}
            ref={register()}
            variant="boxed"
          />
          <Checkbox
            label={t("reports.header.IntermediateReport") as string}
            id={`${name}.reportTypes_intermediate`}
            name={`${name}.reportTypes`}
            value={"IntermediateReport"}
            defaultChecked={reportTypes.includes("IntermediateReport")}
            ref={register()}
            variant="boxed"
          />
          <Checkbox
            label={t("reports.header.FinalReport") as string}
            id={`${name}.reportTypes_final`}
            name={`${name}.reportTypes`}
            value={"FinalReport"}
            defaultChecked={reportTypes.includes("FinalReport")}
            ref={register()}
            variant="boxed"
          />
        </CheckboxGroup>
        <div className="flex flex-col justify-end">
          <Button
            variant="subtle"
            onClick={onRemove}
            trailingIcon={DeleteIcon}
            id={`${name}.deleteQuestion`}
          >
            {t("forms.actions.delete")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default React.memo(TemplateQuestionFormFooter);
